import { useQuery } from "@tanstack/react-query";
import { isMock, getJson, postJson } from "./client";

export interface ScoreBand {
  lower: number;
  upper: number;
  count: number;
}

export interface ScoreDistributionResponse {
  scale: { min: number; max: number; pass: number };
  population: number[];
  bands: ScoreBand[];
  liveApprovalRatePct: number;
}

export interface ApiPendingChange {
  id: string;
  proposedScore: number;
  proposedApprovalRatePct: number;
  proposedBy: string;
  proposedAt: string;
  reason: string;
}

export interface ThresholdResponse {
  liveThreshold: number;
  liveApprovalRatePct: number;
  lastChangedAt: string;
  lastChangedBy: string;
  pendingChange: ApiPendingChange | null;
}

export interface ThresholdHistoryEntry {
  id: string;
  status: "LIVE" | "SUPERSEDED" | "REJECTED" | "PENDING";
  threshold: number;
  approvalRatePct: number;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  proposedBy: string;
  proposedAt: string;
  reason: string;
  decisionBy: string;
  decisionAt: string;
  decisionNotes: string;
}

export interface ThresholdHistoryResponse {
  history: ThresholdHistoryEntry[];
}

// ── Mock fixtures ─────────────────────────────────────────────────────────────

function buildMockPopulation(): number[] {
  const scores: number[] = [];
  const fill = (lo: number, hi: number, n: number) => {
    const step = (hi - lo) / n;
    for (let i = 0; i < n; i++) scores.push(Math.round(lo + step * i));
  };
  fill(800, 1000, 1200);
  fill(750, 800,  750);
  fill(700, 750,  510);
  fill(650, 700,  330);
  fill(600, 650,   90);
  fill(550, 600,   60);
  fill(400, 550,   35);
  fill(250, 400,   20);
  fill(50,  250,    5);
  return scores;
}

const MOCK_POPULATION = buildMockPopulation();

function buildBands(population: number[]): ScoreBand[] {
  const bands: ScoreBand[] = [];
  for (let lower = 0; lower < 1000; lower += 50) {
    const upper = lower + 50;
    bands.push({ lower, upper, count: population.filter(s => s >= lower && s < upper).length });
  }
  return bands;
}

const MOCK_SCORE_DIST: ScoreDistributionResponse = {
  scale: { min: 0, max: 1000, pass: 650 },
  population: MOCK_POPULATION,
  bands: buildBands(MOCK_POPULATION),
  liveApprovalRatePct: 93.0,
};

const MOCK_THRESHOLD: ThresholdResponse = {
  liveThreshold: 650,
  liveApprovalRatePct: 93.0,
  lastChangedAt: "2026-03-12T09:30:00+00:00",
  lastChangedBy: "Roger Barkley",
  pendingChange: null,
};

const MOCK_HISTORY: ThresholdHistoryResponse = {
  history: [
    {
      id: "3",
      status: "LIVE",
      threshold: 650,
      approvalRatePct: 93.0,
      effectiveFrom: "2026-03-12T09:30:00+00:00",
      effectiveTo: null,
      proposedBy: "Scott Adams",
      proposedAt: "2026-03-12T09:30:00+00:00",
      reason: "Raising threshold after two months of elevated tail risk.",
      decisionBy: "Roger Barkley",
      decisionAt: "2026-03-12T09:30:00+00:00",
      decisionNotes: "Approved. Monitor approval rate weekly for the first month.",
    },
    {
      id: "2",
      status: "REJECTED",
      threshold: 620,
      approvalRatePct: 94.5,
      effectiveFrom: "2026-02-18T09:40:00+00:00",
      effectiveTo: "2026-02-18T16:12:00+00:00",
      proposedBy: "Scott Adams",
      proposedAt: "2026-02-18T09:40:00+00:00",
      reason: "Lower threshold to offset volume decline.",
      decisionBy: "Roger Barkley",
      decisionAt: "2026-02-18T16:12:00+00:00",
      decisionNotes: "Too aggressive given current delinquency trend. Revisit after March data.",
    },
    {
      id: "1",
      status: "SUPERSEDED",
      threshold: 640,
      approvalRatePct: 93.5,
      effectiveFrom: "2026-01-09T10:05:00+00:00",
      effectiveTo: "2026-03-12T09:30:00+00:00",
      proposedBy: "Scott Adams",
      proposedAt: "2026-01-09T10:05:00+00:00",
      reason: "Initial threshold set at model deployment.",
      decisionBy: "Roger Barkley",
      decisionAt: "2026-01-09T10:05:00+00:00",
      decisionNotes: "Agreed. Tail risk has been elevated for two consecutive months.",
    },
  ],
};

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function useScoreDistribution(modelId: number) {
  return useQuery({
    queryKey: ["score-distribution", modelId],
    queryFn: () =>
      isMock
        ? Promise.resolve(MOCK_SCORE_DIST)
        : getJson<ScoreDistributionResponse>(
            `risk-appetite-management/score-distribution?model_id=${modelId}`,
          ),
  });
}

// ── Pipeline by Score (from model_observability, filterable) ────────────────────

export interface PipelineScoreDistResponse {
  scale: { min: number; max: number; pass: number };
  bands: ScoreBand[];
  total: number;
  modelVersion: string;
  scoringType: string;
  segment: string;
}

const MOCK_PIPELINE_BANDS: ScoreBand[] = [
  2, 124, 1043, 2745, 6639, 14583, 35828, 90705, 105984,
].map((count, i) => ({ lower: 450 + i * 50, upper: 500 + i * 50, count }));

export function useScoreDistributionByScoring(
  segment: string,
  modelVersion: string,
  scoringType: string,
) {
  return useQuery<PipelineScoreDistResponse>({
    queryKey: ["pipeline-score-distribution", segment, modelVersion, scoringType],
    enabled: Boolean(modelVersion) && Boolean(scoringType),
    queryFn: async () => {
      if (isMock)
        return {
          scale: { min: 450, max: 900, pass: 650 },
          bands: MOCK_PIPELINE_BANDS,
          total: MOCK_PIPELINE_BANDS.reduce((a, b) => a + b.count, 0),
          modelVersion,
          scoringType,
          segment,
        };
      return getJson<PipelineScoreDistResponse>(
        `risk-appetite-management/pipeline-score-distribution?model_version=${encodeURIComponent(
          modelVersion,
        )}&scoring_type=${encodeURIComponent(scoringType)}&segment=${encodeURIComponent(
          segment,
        )}`,
      );
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useThreshold(modelId: number) {
  return useQuery({
    queryKey: ["threshold", modelId],
    queryFn: () =>
      isMock
        ? Promise.resolve(MOCK_THRESHOLD)
        : getJson<ThresholdResponse>(
            `risk-appetite-management/threshold?model_id=${modelId}`,
          ),
  });
}

export function useThresholdHistory(modelId: number) {
  return useQuery({
    queryKey: ["threshold-history", modelId],
    queryFn: () =>
      isMock
        ? Promise.resolve(MOCK_HISTORY)
        : getJson<ThresholdHistoryResponse>(
            `risk-appetite-management/threshold/history?model_id=${modelId}`,
          ),
  });
}

export async function proposeThreshold(body: {
  model_id: number;
  proposedScore: number;
  reason: string;
  proposedBy: string;
}) {
  if (isMock) return Promise.resolve({ status: "success", data: { approvalId: 99, thresholdId: 99 } });
  return postJson<{ status: string; data: { approvalId: number; thresholdId: number } }>(
    "risk-appetite-management/threshold/propose",
    body,
  );
}

export async function decideThreshold(body: {
  model_id: number;
  pendingId: string;
  decision: "APPROVE" | "REJECT";
  decisionBy: string;
  decisionNotes: string;
}) {
  if (isMock) return Promise.resolve({ status: "success", data: { decision: body.decision } });
  return postJson<{ status: string; data: { decision: string } }>(
    "risk-appetite-management/threshold/decide",
    body,
  );
}

// ── Model Risk Tier ───────────────────────────────────────────────────────────

interface RiskTierRow {
  year: string;
  CRITICAL: number;
  HIGH: number;
  MEDIUM: number;
  LOW: number;
}

export interface RiskTierResponse {
  modelType: string;
  modelVersion: string | null;
  modelStamp: string | null;
  rows: RiskTierRow[];
}

const MOCK_RISK_TIER: RiskTierResponse = {
  modelType: "PG",
  modelVersion: "challenger1.3",
  modelStamp: "challenger1.3_PG_JPS_650",
  rows: [
    { year: "2018", CRITICAL: 2.0,  HIGH: 8.0,  MEDIUM: 44.0, LOW: 46.0 },
    { year: "2019", CRITICAL: 3.0,  HIGH: 10.0, MEDIUM: 42.0, LOW: 45.0 },
    { year: "2020", CRITICAL: 5.0,  HIGH: 15.0, MEDIUM: 40.0, LOW: 40.0 },
    { year: "2021", CRITICAL: 7.0,  HIGH: 18.0, MEDIUM: 38.0, LOW: 37.0 },
    { year: "2022", CRITICAL: 10.0, HIGH: 22.0, MEDIUM: 37.0, LOW: 31.0 },
    { year: "2023", CRITICAL: 12.0, HIGH: 25.0, MEDIUM: 36.0, LOW: 27.0 },
    { year: "2024", CRITICAL: 13.0, HIGH: 28.0, MEDIUM: 33.0, LOW: 26.0 },
    { year: "2025", CRITICAL: 14.0, HIGH: 30.0, MEDIUM: 32.0, LOW: 24.0 },
    { year: "2026", CRITICAL: 15.0, HIGH: 32.0, MEDIUM: 31.0, LOW: 22.0 },
  ],
};

export function useModelRiskTier(modelType: string) {
  return useQuery<RiskTierResponse>({
    queryKey: ["model-risk-tier", modelType],
    queryFn: async () => {
      if (isMock) return MOCK_RISK_TIER;
      return getJson<RiskTierResponse>(
        `risk-appetite-management/model-risk-tier?model_type=${modelType}`,
      );
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ── Challenger Model Score Chart ────────────────────────────────────────────────

export interface ChallengerScoreChartRow {
  threshold: number;
  totalApplications: number;
  approvedScoreBased: number;
  avertedChargeoffsScoreBased: number;
  totalDefaults: number;
  netLossScoreBased: number;
  interestEarnedScoreBased: number;
}

export interface ChallengerScoreChartResponse {
  modelType: string;
  modelId: string;
  scoringType: string;
  rows: ChallengerScoreChartRow[];
}

export interface ChallengerChartOptions {
  modelIds: string[];
  scoringTypes: string[];
  minThreshold: number;
  maxThreshold: number;
}

export interface ChallengerCohortRow {
  cohort: number;
  approved: number;
  totalApplications: number;
  totalDefaults: number;
  avertedChargeoffs: number;
  netLossScoreBased: number;
  interestEarnedScoreBased: number;
}

export interface ChallengerCohortsResponse {
  modelType: string;
  modelId: string;
  scoringType: string;
  threshold: number;
  rows: ChallengerCohortRow[];
}

const MOCK_CHALLENGER_OPTIONS: ChallengerChartOptions = {
  modelIds: ["challenger1.3", "challenger1.3.1"],
  scoringTypes: ["DPS", "FPS", "JPS"],
  minThreshold: 450,
  maxThreshold: 900,
};

function buildMockChallengerChart(): ChallengerScoreChartRow[] {
  const rows: ChallengerScoreChartRow[] = [];
  for (let t = 450; t <= 900; t += 5) {
    const p = (t - 450) / (900 - 450); // 0 → 1 across the sweep
    const approved = Math.round(105000 * (1 - p) ** 3);
    const averted = Math.round(100000 * (1 - (1 - p) ** 2));
    rows.push({
      threshold: t,
      totalApplications: 105000,
      // approved falls as the threshold rises (light-blue line)
      approvedScoreBased: approved,
      // overlaps rise and plateau as the threshold rises (dark-blue line)
      avertedChargeoffsScoreBased: averted,
      // predicted defaults ≈ 5% of approved, plus the averted chargeoffs
      totalDefaults: Math.round(approved * 0.05) + averted,
      // economics (currency-ish): loss averted grows with threshold, interest
      // foregone grows faster → margin crosses zero across the sweep
      netLossScoreBased: Math.round(averted * 45),
      interestEarnedScoreBased: Math.round(averted * 40 + (105000 - approved) * 12),
    });
  }
  return rows;
}

function buildMockChallengerCohorts(threshold: number): ChallengerCohortRow[] {
  const rows: ChallengerCohortRow[] = [];
  const p = Math.min(Math.max((threshold - 450) / (900 - 450), 0), 1);
  for (let year = 2018; year <= 2026; year++) {
    const totalApplications = 6000 + (year - 2018) * 300;
    const approved = Math.round(totalApplications * (1 - p) ** 3 * 0.98);
    const averted = Math.round(approved * 0.01 * p);
    rows.push({
      cohort: year,
      approved,
      totalApplications,
      totalDefaults: Math.round(approved * 0.05) + averted,
      avertedChargeoffs: averted,
      netLossScoreBased: Math.round(averted * 45),
      interestEarnedScoreBased: Math.round(averted * 40 + (totalApplications - approved) * 12),
    });
  }
  return rows;
}

const MOCK_CHALLENGER_CHART_ROWS = buildMockChallengerChart();

export function useChallengerChartOptions() {
  return useQuery<ChallengerChartOptions>({
    queryKey: ["challenger-chart-options"],
    queryFn: async () => {
      if (isMock) return MOCK_CHALLENGER_OPTIONS;
      return getJson<ChallengerChartOptions>(
        "risk-appetite-management/challenger-model-score-chart/options",
      );
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useChallengerScoreChart(
  modelType: string,
  modelId: string,
  scoringType: string,
) {
  return useQuery<ChallengerScoreChartResponse>({
    queryKey: ["challenger-score-chart", modelType, modelId, scoringType],
    enabled: Boolean(modelId) && Boolean(scoringType),
    queryFn: async () => {
      if (isMock)
        return {
          modelType,
          modelId,
          scoringType,
          rows: MOCK_CHALLENGER_CHART_ROWS,
        };
      return getJson<ChallengerScoreChartResponse>(
        `risk-appetite-management/challenger-model-score-chart?model_type=${encodeURIComponent(
          modelType,
        )}&model_id=${encodeURIComponent(modelId)}&scoring_type=${encodeURIComponent(
          scoringType,
        )}`,
      );
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useChallengerScoreCohorts(
  modelType: string,
  modelId: string,
  scoringType: string,
  threshold: number,
) {
  return useQuery<ChallengerCohortsResponse>({
    queryKey: ["challenger-score-cohorts", modelType, modelId, scoringType, threshold],
    enabled: Boolean(modelId) && Boolean(scoringType) && threshold > 0,
    queryFn: async () => {
      if (isMock)
        return {
          modelType,
          modelId,
          scoringType,
          threshold,
          rows: buildMockChallengerCohorts(threshold),
        };
      return getJson<ChallengerCohortsResponse>(
        `risk-appetite-management/challenger-model-score-cohorts?model_type=${encodeURIComponent(
          modelType,
        )}&model_id=${encodeURIComponent(modelId)}&scoring_type=${encodeURIComponent(
          scoringType,
        )}&threshold=${threshold}`,
      );
    },
    staleTime: 5 * 60 * 1000,
  });
}
